import React from "react";
import {
  CompositeDecorator,
  ContentState,
  EditorState,
  Modifier,
  SelectionState,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { ENTITY_TYPE } from "draft-js-utils";
import * as linkify from "linkifyjs";

const LINK_ENTITY = ENTITY_TYPE.LINK;

export function normalizeStoredUrl(raw: string): string {
  const trimmed = raw.replace(/\s/g, "");
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

export function isAcceptableLinkUrl(raw: string): boolean {
  const trimmed = raw.replace(/\s/g, "");
  if (!trimmed) return false;
  const hits = linkify.find(trimmed);
  if (hits.some((h) => h.type === "url")) return true;
  return /^[\w.-]+\.[a-z]{2,}([\w\-./?#%&+=:~@]*)?$/i.test(trimmed);
}

export function getSelectedPlainText(
  contentState: ContentState,
  selection: SelectionState
): string {
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();
  const startBlock = contentState.getBlockForKey(startKey);
  const endBlock = contentState.getBlockForKey(endKey);

  if (!startBlock || !endBlock) return "";

  if (startKey === endKey) {
    return startBlock.getText().slice(startOffset, endOffset);
  }

  const blocks = contentState.getBlocksAsArray();
  const parts: string[] = [];
  let capturing = false;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const key = block.getKey();

    if (key === startKey) {
      capturing = true;
      parts.push(block.getText().slice(startOffset));
    } else if (key === endKey) {
      parts.push(block.getText().slice(0, endOffset));
      break;
    } else if (capturing) {
      parts.push(block.getText());
    }
  }

  return parts.join("\n");
}

function findLinkEntities(
  contentBlock: { findEntityRanges: Function },
  callback: (start: number, end: number) => void,
  contentState: ContentState
) {
  contentBlock.findEntityRanges(
    (character: { getEntity: () => string | null }) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === LINK_ENTITY
      );
    },
    callback
  );
}

interface RteLinkSpanProps {
  contentState: ContentState;
  entityKey: string;
  children: React.ReactNode;
}

const RteLinkSpan: React.FC<RteLinkSpanProps> = ({
  contentState,
  entityKey,
  children,
}) => {
  const { url } = contentState.getEntity(entityKey).getData();
  return (
    <a
      href={url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.preventDefault()}
      style={{ color: "#2563eb", textDecoration: "underline", cursor: "text" }}
    >
      {children}
    </a>
  );
};

export const rteLinkDecorator = new CompositeDecorator([
  { strategy: findLinkEntities, component: RteLinkSpan },
]);

export function createRteEditorState(contentState: ContentState): EditorState {
  return EditorState.createWithContent(contentState, rteLinkDecorator);
}

export function ensureRteDecorator(editorState: EditorState): EditorState {
  if (editorState.getDecorator() === rteLinkDecorator) {
    return editorState;
  }
  return EditorState.set(editorState, { decorator: rteLinkDecorator });
}

export function rteContentStateFromHtml(html: string): ContentState {
  return stateFromHTML(html || "");
}

export function rteHtmlFromContentState(contentState: ContentState): string {
  return stateToHTML(contentState);
}

export function getLinkEntityKeyAtSelection(
  editorState: EditorState
): string | null {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const blockKey = selection.getStartKey();
  const block = contentState.getBlockForKey(blockKey);
  const offsets = selection.isCollapsed()
    ? [selection.getStartOffset()]
    : [selection.getStartOffset(), selection.getEndOffset() - 1];

  for (let i = 0; i < offsets.length; i++) {
    const entityKey = block.getEntityAt(Math.max(0, offsets[i]));
    if (entityKey == null) continue;
    if (contentState.getEntity(entityKey).getType() === LINK_ENTITY) {
      return entityKey;
    }
  }

  return null;
}

export function isLinkActiveAtSelection(editorState: EditorState): boolean {
  return getLinkEntityKeyAtSelection(editorState) !== null;
}

export function applyLinkToEditorState(
  editorState: EditorState,
  preview: string,
  url: string,
  entityKey: string | null
): EditorState {
  const normalizedUrl = normalizeStoredUrl(url);
  let contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const normalizedSelection = selection.merge({
    isBackward: false,
    hasFocus: true,
  });

  contentState = Modifier.replaceText(
    contentState,
    normalizedSelection,
    preview
  );

  const blockKey = normalizedSelection.getAnchorKey();
  const insertStart = normalizedSelection.getStartOffset();
  const linkSelection = SelectionState.createEmpty(blockKey).merge({
    anchorOffset: insertStart,
    focusOffset: insertStart + preview.length,
    hasFocus: true,
    isBackward: false,
  });

  if (entityKey) {
    contentState = contentState.mergeEntityData(entityKey, {
      url: normalizedUrl,
      preview,
      target: "_blank",
      rel: "noopener noreferrer",
    });
    contentState = Modifier.applyEntity(contentState, linkSelection, entityKey);
  } else {
    contentState = contentState.createEntity(LINK_ENTITY, "MUTABLE", {
      url: normalizedUrl,
      preview,
      target: "_blank",
      rel: "noopener noreferrer",
    });
    const newEntityKey = contentState.getLastCreatedEntityKey();
    contentState = Modifier.applyEntity(
      contentState,
      linkSelection,
      newEntityKey
    );
  }

  const nextState = EditorState.push(
    editorState,
    contentState,
    "apply-entity"
  );
  return EditorState.forceSelection(nextState, linkSelection);
}

export function removeLinkFromEditorState(
  editorState: EditorState,
  entityKey: string
): EditorState {
  let contentState = editorState.getCurrentContent();

  contentState.getBlockMap().forEach((block) => {
    block.findEntityRanges(
      (character: { getEntity: () => string | null }) =>
        character.getEntity() === entityKey,
      (start: number, end: number) => {
        const selection = SelectionState.createEmpty(block.getKey()).merge({
          anchorOffset: start,
          focusOffset: end,
        });
        contentState = Modifier.applyEntity(contentState, selection, null);
      }
    );
  });

  return EditorState.push(editorState, contentState, "apply-entity");
}

export function deriveLinkModalFields(
  editorState: EditorState,
  entityKey: string | null
): { preview: string; url: string } {
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const selectedText = selection.isCollapsed()
    ? ""
    : getSelectedPlainText(contentState, selection);

  const urlList = linkify.find(selectedText);
  let url =
    urlList.length === 1 && urlList[0].type === "url" ? urlList[0].href : "";
  let preview = selectedText;

  if (entityKey) {
    const entity = contentState.getEntity(entityKey);
    const entityData = entity.getData();
    url = entityData.url || "";
    if (typeof entityData.preview === "string" && entityData.preview.length > 0) {
      preview = entityData.preview;
    } else {
      const block = contentState.getBlockForKey(selection.getStartKey());
      block.findEntityRanges(
        (character: { getEntity: () => string | null }) =>
          character.getEntity() === entityKey,
        (start: number, end: number) => {
          preview = block.getText().slice(start, end);
        }
      );
    }
  }

  return { preview, url };
}
